<template>
  <aside class="wa__sidebar">
    <div class="wa__search">
      <div class="wa__search-box">
        <i class="fas fa-search wa__search-icon"></i>
        <input
          :value="searchQuery"
          type="text"
          class="wa__search-input"
          placeholder="Buscar por empleado o teléfono..."
          @input="$emit('update:searchQuery', $event.target.value)"
        />
      </div>
    </div>

    <div v-if="loading" class="wa__sidebar-loading">
      <i class="fas fa-spinner fa-pulse"></i>
      <span>Cargando conversaciones…</span>
    </div>

    <div v-else-if="firestoreError" class="wa__sidebar-error">
      <i class="fas fa-exclamation-triangle"></i>
      <span>Error al cargar conversaciones</span>
      <small>{{ firestoreError }}</small>
    </div>

    <div v-else class="wa__conversations">
      <div
        v-for="conv in conversations"
        :key="conv.phoneNumber"
        class="wa__conv-item"
        :class="{
          'wa__conv-item--active': conv.phoneNumber === selectedPhone,
        }"
        @click="$emit('select-conversation', conv)"
      >
        <div
          class="wa__avatar"
          :style="{ backgroundColor: avatarColor(conv.phoneNumber) }"
        >
          {{ getInitials(employeeName(conv)) }}
        </div>
        <div class="wa__conv-body">
          <div class="wa__conv-top">
            <span class="wa__conv-name">
              {{ employeeName(conv) }}
            </span>
            <span class="wa__conv-time">
              {{ relativeTime(conv.lastMessageAt || conv.updatedAt) }}
            </span>
          </div>
          <div class="wa__conv-ref">
            {{ conv.phoneNumber }}
            <template v-if="conv.profile?.branch">
              · {{ conv.profile.branch }}
            </template>
          </div>
          <div class="wa__conv-bottom">
            <span class="wa__conv-preview">
              {{ lastMessagePreview(conv.messages) }}
            </span>
            <span
              class="wa__status-dot"
              :style="{ backgroundColor: statusDotColor(conv) }"
              :title="modeLabel(conv.mode)"
            ></span>
          </div>
        </div>
      </div>

      <div v-if="conversations.length === 0" class="wa__no-results">
        No se encontraron conversaciones
      </div>
    </div>
  </aside>
</template>

<script>
export default {
  name: "WaSidebar",
  props: {
    conversations: {
      type: Array,
      required: true,
    },
    selectedPhone: {
      type: String,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    firestoreError: {
      type: String,
      default: null,
    },
    searchQuery: {
      type: String,
      required: true,
    },
  },
  emits: ["select-conversation", "update:searchQuery"],
  methods: {
    employeeName(conv) {
      if (conv.employeeDisplayName) return conv.employeeDisplayName;
      const names = conv.profile?.names || "";
      const lastnames = conv.profile?.lastnames || "";
      const full = `${names} ${lastnames}`.trim();
      return full || conv.employeeId || conv.phoneNumber || "—";
    },
    getInitials(name) {
      if (!name) return "?";
      const parts = String(name).trim().split(/\s+/);
      if (parts.length === 1) return (parts[0][0] || "?").toUpperCase();
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    },
    avatarColor(phone) {
      if (!phone) return "#8696a0";
      let hash = 0;
      for (let i = 0; i < phone.length; i++) {
        hash = phone.charCodeAt(i) + ((hash << 5) - hash);
      }
      return `hsl(${Math.abs(hash) % 360}, 45%, 42%)`;
    },
    statusDotColor(conv) {
      if (conv.mode === "human") return "#FF9500";
      if (conv.mode === "paused") return "#8696A0";
      if (conv.status === "active") return "#25D366";
      if (conv.status === "closed") return "#3B82F6";
      return "#8696A0";
    },
    modeLabel(mode) {
      if (mode === "human") return "Modo asesor";
      if (mode === "paused") return "Pausado";
      return "Bot activo";
    },
    toMs(timestamp) {
      if (!timestamp) return 0;
      if (typeof timestamp.toMillis === "function") return timestamp.toMillis();
      if (timestamp.seconds) return timestamp.seconds * 1000;
      return new Date(timestamp).getTime() || 0;
    },
    relativeTime(timestamp) {
      if (!timestamp) return "";
      const ms = this.toMs(timestamp);
      const diffMs = Date.now() - ms;
      const diffMin = Math.floor(diffMs / 60000);
      if (diffMin < 1) return "ahora";
      if (diffMin < 60) return `hace ${diffMin} min`;
      const diffHours = Math.floor(diffMs / 3600000);
      if (diffHours < 24) {
        return `hace ${diffHours} ${diffHours === 1 ? "hora" : "horas"}`;
      }
      const diffDays = Math.floor(diffMs / 86400000);
      if (diffDays === 1) return "ayer";
      const date = new Date(ms);
      return date.toLocaleDateString("es-EC", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      });
    },
    extractText(content) {
      if (typeof content === "string") return content;
      if (Array.isArray(content)) {
        return content
          .filter((b) => b.type === "text")
          .map((b) => b.text)
          .join("\n");
      }
      return "";
    },
    lastMessagePreview(messages) {
      if (!messages || messages.length === 0) return "";
      for (let i = messages.length - 1; i >= 0; i--) {
        const last = messages[i];
        if (Array.isArray(last.content)) continue;
        if (last.type === "location" || this.isLocationText(last.content)) {
          return "📍 Ubicación";
        }
        if (last.isVoiceNote || last.type === "audio") {
          return "🎤 Nota de voz";
        }
        const text = this.extractText(last.content);
        if (!text) continue;
        return text.length > 40 ? text.substring(0, 40) + "…" : text;
      }
      return "";
    },
    isLocationText(content) {
      const text = typeof content === "string" ? content : "";
      return (
        text.includes("He compartido la ubicación") ||
        text.includes("He compartido mi ubicación")
      );
    },
  },
};
</script>
